// @flow
import routerActions from './router';
import type {State as ReduxState} from '../reducers/index';
import type {MapViewport} from '../components/MapContainer/MapContainer';

export const MAP_ADD_MARKER_CLICKED = 'MAP/ADD_MARKER_CLICKED';
export const MAP_CHANGE_HIT_PREVIEW = 'MAP/CHANGE_HIT_PREVIEW';
export const MAP_CLEAR_HIGHLIGHTED_PINS = 'MAP/CLEAR_HIGHLIGHTED_PINS';
export const MAP_DISABLE_SEARCH_ON_MOVE = 'MAP/DISABLE_SEARCH_ON_MOVE';
export const MAP_ENABLE_SEARCH_ON_MOVE = 'MAP/ENABLE_SEARCH_ON_MOVE';
export const MAP_HIGHLIGHT_PIN = 'MAP/HIGHLIGHT_PIN';
export const MAP_IDLE = 'MAP/IDLE';
export const MAP_LOADED = 'MAP/LOADED';
export const MAP_RESET_SEARCH_BUTTON = 'MAP/RESET_SEARCH_BUTTON';
export const MAP_SHOW_SEARCH_BUTTON = 'MAP/SHOW_SEARCH_BUTTON';
export const MAP_SYNC_BOUNDS = 'MAP/SYNC_BOUNDS';
export const MAP_UPDATE = 'MAP/CHANGE_UPDATE';
export const MAP_SET_MOVE_STATE = 'MAP/SET_MOVE_STATE';
export const MAP_RESET_TO_INITIAL_BOUNDS = 'MAP/RESET_TO_INITIAL_BOUNDS';

type MapAddPinClickedActionType = 'MAP/ADD_MARKER_CLICKED';
type MapChangeHitPreviewActionType = 'MAP/CHANGE_HIT_PREVIEW';
type MapClearHighlightedPinsActionType = 'MAP/CLEAR_HIGHLIGHTED_PINS';
type MapDisableSearchOnMoveType = 'MAP/DISABLE_SEARCH_ON_MOVE';
type MapEnableSearchOnMoveType = 'MAP/ENABLE_SEARCH_ON_MOVE';
type MapHighlightPinActionType = 'MAP/HIGHLIGHT_PIN';
type MapIdleActionType = 'MAP/IDLE';
type MapLoadedActionType = 'MAP/LOADED';
type MapResetSearchButtonAction = 'MAP/RESET_SEARCH_BUTTON';
type MapShowSearchButtonActionType = 'MAP/SHOW_SEARCH_BUTTON';
type MapSyncBoundsActionType = 'MAP/SYNC_BOUNDS';
type MapUpdateActionType = 'MAP/CHANGE_UPDATE';
type MapSetMoveStateActionType = 'MAP/SET_MOVE_STATE';
type MapResetToInitialBoundsActionType = 'MAP/RESET_TO_INITIAL_BOUNDS';

export type ActionType =
    | MapAddPinClickedActionType
    | MapChangeHitPreviewActionType
    | MapClearHighlightedPinsActionType
    | MapDisableSearchOnMoveType
    | MapEnableSearchOnMoveType
    | MapHighlightPinActionType
    | MapIdleActionType
    | MapLoadedActionType
    | MapResetSearchButtonAction
    | MapShowSearchButtonActionType
    | MapSyncBoundsActionType
    | MapUpdateActionType
    | MapSetMoveStateActionType
    | MapResetToInitialBoundsActionType;

type MapHighlightPinAction = {
    type: MapHighlightPinActionType,
    payload: {
        listingId: string
    }
};

type MapClearHighlightedPinsAction = {
    type: MapClearHighlightedPinsActionType
};

type MapLoadedAction = {
    type: MapLoadedActionType,
    payload: {
        zoom: number
    }
};

type MapShowSearchButtonAction = {
    type: MapShowSearchButtonActionType
};

type MapIdleActionTypeAction = {
    type: MapIdleActionType
};

type MapResetSearchButtonActionTypeAction = {
    type: MapResetSearchButtonAction
};

type MapChangeHitPreviewActionTypeAction = {
    type: MapChangeHitPreviewActionType,
    payload: {
        currentHitPrev: ?string
    }
};

type MapSyncBoundsAction = {
    type: MapSyncBoundsActionType,
    payload: {
        bounds: MapViewport,
        mapOptions?: Object
    }
};

type MapUpdateAction = {
    type: MapUpdateActionType,
    payload: {
        bounds: MapViewport,
        zoom: number
    }
};

type MapEnableSearchOnMoveAction = {
    type: MapEnableSearchOnMoveType
};

type MapDisableSearchOnMoveAction = {
    type: MapDisableSearchOnMoveType
};

type MapAddPinClickedAction = {
    type: MapAddPinClickedActionType,
    payload: {
        listingId: string
    }
};

type MapSetMoveStateAction = {
    type: MapSetMoveStateActionType,
    payload: {
        mapMoveState: boolean
    }
};

type MapResetToInitialBoundsAction = {
    type: MapResetToInitialBoundsActionType
};

export type Action =
    | MapAddPinClickedAction
    | MapChangeHitPreviewActionTypeAction
    | MapClearHighlightedPinsAction
    | MapDisableSearchOnMoveAction
    | MapEnableSearchOnMoveAction
    | MapHighlightPinAction
    | MapIdleActionTypeAction
    | MapLoadedAction
    | MapResetSearchButtonActionTypeAction
    | MapResetToInitialBoundsAction
    | MapSetMoveStateAction
    | MapShowSearchButtonAction
    | MapSyncBoundsAction
    | MapUpdateAction;

const ACTION_CREATORS = {
    highlightPin(listingId: string): MapHighlightPinAction {
        return {
            type: MAP_HIGHLIGHT_PIN,
            payload: {listingId}
        };
    },
    clearHighlightedPins(): MapClearHighlightedPinsAction {
        return {
            type: MAP_CLEAR_HIGHLIGHTED_PINS
        };
    },
    addPinClicked(listingId: string): MapAddPinClickedAction {
        return {
            type: MAP_ADD_MARKER_CLICKED,
            payload: {listingId}
        };
    },
    changeHitPreview(
        currentHitPrev: ?string
    ): MapChangeHitPreviewActionTypeAction {
        return {
            type: MAP_CHANGE_HIT_PREVIEW,
            payload: {currentHitPrev}
        };
    },
    mapLoaded(zoom: number): MapLoadedAction {
        return {
            type: MAP_LOADED,
            payload: {
                zoom
            }
        };
    },
    mapShowSearchButton(): MapShowSearchButtonAction {
        return {
            type: MAP_SHOW_SEARCH_BUTTON
        };
    },
    mapIdle(): MapIdleActionTypeAction {
        return {
            type: MAP_IDLE
        };
    },
    mapResetSearchButton(): MapResetSearchButtonActionTypeAction {
        return {
            type: MAP_RESET_SEARCH_BUTTON
        };
    },
    updateMap(bounds: MapViewport, zoom: number) {
        return (dispatch: Function, getState: () => ReduxState) => {
            const {shouldSearchOnMove} = getState().map;

            if (shouldSearchOnMove) {
                dispatch(routerActions.mapSearch({...bounds, zoom}));
            }

            dispatch({
                type: MAP_UPDATE,
                payload: {
                    bounds,
                    zoom
                }
            });
        };
    },
    syncBounds(bounds: MapViewport, mapOptions?: Object): MapSyncBoundsAction {
        return {
            type: MAP_SYNC_BOUNDS,
            payload: {
                bounds,
                mapOptions
            }
        };
    },
    mapResetToInitialBounds(): MapResetToInitialBoundsAction {
        return {
            type: MAP_RESET_TO_INITIAL_BOUNDS
        };
    },
    enableSearchOnMove(): MapEnableSearchOnMoveAction {
        return {
            type: MAP_ENABLE_SEARCH_ON_MOVE
        };
    },
    disableSearchOnMove(): MapDisableSearchOnMoveAction {
        return {
            type: MAP_DISABLE_SEARCH_ON_MOVE
        };
    },
    setMapMoveState(mapMoveState: boolean): MapSetMoveStateAction {
        return {
            type: MAP_SET_MOVE_STATE,
            payload: {
                mapMoveState
            }
        };
    }
};

export default {
    ...ACTION_CREATORS
};
